<div class="sidebar-host flex relative bg-white"
    [ngClass]="fullHeight() ? 'min-h-full h-full' : 'h-full overflow-hidden'">

    <!-- ── Sidebar panel ──────────────────────────────────────────────── -->
    <div [id]="sidebarId()"
        class="sidebar-panel shrink-0 h-inherit border select-none overflow-hidden bg-white dark:bg-surface-900"
        [ngClass]="[
            (sidebarService.getSidebarVisible$(sidebarId()) | async) ? 'w-[15rem] translate-x-0' : 'w-0 -translate-x-full border-0 pointer-events-none',
            fullHeight() ? 'static' : 'static shadow-none',
            isLocked() ? 'sidebar-locked' : ''
        ]">
        <div class="flex flex-col h-full w-full min-h-0">

            <!-- Header slot: workspace selector (always visible) -->
            @if (sidebarHeader) {
            <div class="flex-shrink-0">
                <ng-container *ngTemplateOutlet="sidebarHeader" />
            </div>
            }

            <!-- Back button row (only when in a sub-view, e.g. bob / settings) -->
            @if (isSubView()) {
            <div class="flex items-center gap-2 p-2 pt-0 flex-shrink-0 border-b border-slate-200">
                <app-button [icon]="'chevron-left'" [iconSource]="'lucide'" [size]="'sm'" (clicked)="navigateBack()"
                    [variant]="'ghost'" [severity]="'contrast'" />
                <span class="text-sm font-semibold text-surface-700 truncate">
                    {{ currentView()?.title }}
                </span>
            </div>
            }

            <!-- Scrollable menu area -->
            <div class="overflow-y-auto flex-1 flex flex-col min-h-0" [ngClass]="fullHeight() ? 'my-2' : 'py-2'">

                @for (menu of currentMenu(); let menuIndex = $index; track menu.header) {
                <ul class="list-none m-0 flex flex-col"
                    [ngClass]="!isMenuCollapsible(menu) && menu.items ? 'flex-1 min-h-0' : ''">
                    <li class="list-none m-0 overflow-hidden flex flex-col gap-0.5 px-2"
                        [ngClass]="[menu.items && menu.items.length > 0 ? 'py-1' : '', !isMenuCollapsible(menu) && menu.items ? 'flex-1 min-h-0' : '']">

                        <!-- Section header / standalone nav item -->
                        <div [routerLink]="menu.locked ? null : (menu.route ?? null)" routerLinkActive="bg-secondary"
                            [routerLinkActiveOptions]="{ exact: menu.exactRoute ?? false }"
                            class="flex items-center gap-2 p-1 rounded-base transition-colors duration-150 flex-shrink-0"
                            [ngClass]="[
                                    (menu.items && menu.items.length > 0) ? 'sb-header-text' : 'sb-item-text',
                                    menu.locked ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer sb-item-hover',
                                    menu.highlighted ? 'outline outline-2 outline-blue-500 -outline-offset-2' : ''
                                ]"
                            (click)="menu.locked ? $event.preventDefault() : (menu.items && menu.items.length > 0 && isMenuCollapsible(menu) ? toggleMenu(menu.header) : menu.click?.())">

                            <span class="inline-flex items-center gap-2 rounded-base">
                                @if (menu.svg) {
                                <span class="sb-icon-wrapper shrink-0">
                                    <app-icon [type]="menu.svg" [source]="'svg'" [size]="'sm'" />
                                </span>
                                }
                                @if (menu.svg_path) {
                                <img [src]="menu.svg_path" alt="" class="w-4 h-4 shrink-0" />
                                }
                                @if (menu.icon) {
                                <span class="sb-icon-wrapper shrink-0">
                                    <app-icon [type]="menu.icon" [source]="'primeng'" [size]="'default'"
                                        [class]="(menu.items && menu.items.length > 0) ? 'sb-header-text' : 'sb-item-text'" />
                                </span>
                                }

                                <span class="text-sm flex items-center gap-2">
                                    {{ menu.header }}
                                    @if (menu.loading) {
                                    <i class="pi pi-spinner pi-spin icon-sm"></i>
                                    } @else if (menu.badge) {
                                    <div class="rounded-lg py-[2px] px-[6px] flex items-center justify-center truncate max-w-[6.25rem] badge-shadow"
                                        [ngClass]="[
                                                    menu.badge.severity ? 'badge-' + menu.badge.severity : '',
                                                    menu.badge.outlined ? 'badge-outlined' : ''
                                                ]">
                                        <span class="text-xs font-medium">{{ menu.badge.label }}</span>
                                    </div>
                                    }
                                    @if (menu.infoClick) {
                                    <span pTooltip="Learn more" tooltipPosition="bottom"
                                        class="flex items-center justify-center rounded-full transition-colors cursor-pointer flex-shrink-0"
                                        (click)="$event.stopPropagation(); $event.preventDefault(); menu.infoClick!()">
                                        <app-icon type="info" source="lucide" size="xs" />
                                    </span>
                                    }
                                </span>
                            </span>

                            @if (menu.locked) {
                            <i class="pi pi-lock text-surface-500 dark:text-surface-400 ml-auto"
                                style="font-size:0.75rem"></i>
                            } @else if (menu.items && menu.items.length > 0 && isMenuCollapsible(menu)) {
                            <app-icon [type]="isMenuCollapsed(menu.header) ? 'chevron-down' : 'chevron-up'"
                                [source]="'lucide'" [size]="'sm'" class="ml-auto text-surface-400" />
                            }
                        </div>

                        <!-- Items container -->
                        <div class="flex flex-col gap-0.5" [ngClass]="{
                                    'overflow-hidden': isMenuCollapsible(menu),
                                    'overflow-y-auto flex-1 min-h-0': !isMenuCollapsible(menu) && !!menu.items,
                                    'hidden': isMenuCollapsible(menu) && isMenuCollapsed(menu.header)
                                }"
                            (scroll)="!isMenuCollapsible(menu) && menu.items ? onMenuItemsScroll($event) : null">

                            <!-- Create shortcut link -->
                            @if (menu.create) {
                            <a [routerLink]="menu.locked ? null : menu.create.route"
                                [queryParams]="menu.create.queryParams" routerLinkActive="bg-secondary"
                                [routerLinkActiveOptions]="{ exact: false }"
                                class="flex items-center gap-2 p-1 rounded-base transition-colors duration-150"
                                [ngClass]="menu.locked ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer sb-item-hover'"
                                (click)="menu.locked ? $event.preventDefault() : menu.create.click?.()">
                                @if (menu.create.svg) {
                                <span class="sb-icon-wrapper shrink-0">
                                    <app-icon [type]="menu.create.svg" [source]="'svg'" [size]="'sm'"
                                        [class]="menu.locked ? 'opacity-50' : 'theme-text'" />
                                </span>
                                }
                                @if (menu.create.icon) {
                                <span class="sb-icon-wrapper shrink-0">
                                    <app-icon [type]="menu.create.icon" [source]="'primeng'" [size]="'default'"
                                        [class]="menu.locked ? 'opacity-50' : 'theme-text'" />
                                </span>
                                }
                                <span class="text-sm" [ngClass]="menu.locked ? 'opacity-50' : 'theme-text'">
                                    {{ menu.create.label | textCase }}
                                </span>
                                @if (menu.locked) {
                                <i class="pi pi-lock text-surface-500 dark:text-surface-400 ml-auto"
                                    style="font-size:0.75rem"></i>
                                }
                            </a>
                            }

                            <!-- Menu items list -->
                            <ul class="list-none m-0 flex flex-col gap-0">
                                @for (item of menu.items; track item.label + '-' + $index; let itemIdx = $index) {
                                <li class="leading-tight">
                                    <div class="sb-menu-item-container flex items-center rounded-base transition-colors duration-150 min-h-6 pl-1 py-0.5"
                                        [ngClass]="menu.locked ? 'opacity-50' : 'sb-item-hover'"
                                        (mouseenter)="setHoveredItem(menu.header + '-' + itemIdx)"
                                        (mouseleave)="setHoveredItem(null)">
                                        <a [routerLink]="menu.locked ? null : item.route"
                                            [queryParams]="item.queryParams" routerLinkActive="sb-active-item"
                                            [routerLinkActiveOptions]="{ exact: false }" [title]="item.label"
                                            class="flex items-center rounded-base gap-2 p-0 flex-1 min-w-0"
                                            [ngClass]="menu.locked ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'"
                                            (click)="menu.locked ? $event.preventDefault() : item.click?.()">
                                            @if (item.svg) {
                                            <span class="sb-icon-wrapper shrink-0">
                                                <app-icon [type]="item.svg" [source]="'svg'"
                                                    [size]="item.iconSize || 'sm'" />
                                            </span>
                                            }
                                            @if (item.icon) {
                                            <span class="sb-icon-wrapper shrink-0">
                                                <app-icon [type]="item.icon" [source]="item.iconSource ?? 'primeng'"
                                                    [size]="item.iconSize || 'default'"
                                                    [class]="item.iconClass ?? ''" />
                                            </span>
                                            }
                                            <span class="text-sm sb-item-text truncate">{{ item.label }}</span>
                                            @if (item.badge) {
                                            <div class="rounded-lg py-[2px] px-[6px] flex items-center justify-center truncate max-w-[6.25rem] badge-shadow"
                                                [ngClass]="[
                                                                item.badge.severity ? 'badge-' + item.badge.severity : '',
                                                                item.badge.outlined ? 'badge-outlined' : ''
                                                            ]">
                                                <span class="text-xs font-medium">{{ item.badge.label }}</span>
                                            </div>
                                            }
                                            @if (menu.locked) {
                                            <i class="pi pi-lock text-surface-500 dark:text-surface-400 ml-auto"
                                                style="font-size:0.75rem"></i>
                                            }
                                        </a>

                                        <!-- Three-dot options menu: always render to prevent layout shift on hover -->
                                        @if (item.options && item.options.length > 0) {
                                        @if (getConvertedMenuItems(menu.header, itemIdx); as opts) {
                                        <div class="flex-shrink-0 w-6 flex items-center justify-end transition-opacity duration-150"
                                            [class.invisible]="!shouldShowOptionsButton(menu.header, itemIdx)"
                                            [class.pointer-events-none]="!shouldShowOptionsButton(menu.header, itemIdx)">
                                            <app-menu #menuRef [items]="opts"
                                                (visibleChange)="onMenuVisibilityChange($event, menu.header, itemIdx)">
                                                <app-button variant="ghost" severity="contrast" icon="pi pi-ellipsis-v"
                                                    [size]="'xxs'" iconSource="primeng" [iconSize]="'xs'"
                                                    (clicked)="menuRef.toggle($event)">
                                                </app-button>
                                            </app-menu>
                                        </div>
                                        }
                                        }
                                    </div>
                                </li>
                                }
                            </ul>

                            <!-- "More / See all" link -->
                            @if (menu.more) {
                            <a class="flex items-center gap-2 p-1 rounded-base transition-colors duration-150"
                                routerLinkActive="bg-secondary" [routerLinkActiveOptions]="{ exact: false }"
                                [routerLink]="menu.locked ? null : menu.more.route"
                                [queryParams]="menu.more.queryParams"
                                [ngClass]="menu.locked ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer sb-item-hover'"
                                (click)="menu.locked ? $event.preventDefault() : null">
                                <span class="sb-more-text text-sm">{{ menu.more.label | textCase }}</span>
                                @if (menu.locked) {
                                <i class="pi pi-lock text-surface-500 dark:text-surface-400 ml-auto"
                                    style="font-size:0.75rem"></i>
                                }
                            </a>
                            }
                        </div>
                    </li>
                </ul>
                }
            </div>

            <!-- Footer slot: user avatar + menu (hidden when in sub-view) -->
            @if (sidebarFooter && showFooter()) {
            <div class="flex-shrink-0">
                <ng-container *ngTemplateOutlet="sidebarFooter" />
            </div>
            }
        </div>
    </div>

    <!-- ── Main content area ──────────────────────────────────────────── -->
    <div class="flex flex-col relative flex-auto h-inherit">
        @if (showTopBar()) {
        <app-top-bar class="top-bar" [sidebarId]="sidebarId()" />
        }
        <div class="flex flex-col flex-auto overflow-hidden h-full min-h-0">
            <router-outlet />
        </div>
    </div>

</div>